/**
 * Виджет «Структура портфеля по видам работ» (FR-4): кольцевая диаграмма
 * сумм / количества договоров по видам (БИП · СМР · ПСД · Надзор и
 * экспертиза · Мероприятия) с переключателями «План / Факт» и
 * «Сумма / Кол-во» (FR-4.2).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DonutChart } from './DonutChart';
import { CHART_COLORS, fmt, fmt1, moneyMln } from './format';
import { SegToggle } from './SegToggle';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import type { ProjectsSlice } from '../../../types/projects';

type PlanFact = 'plan' | 'fact';
type SumCount = 'sum' | 'count';

interface PortfolioWidgetProps {
  works: ProjectsSlice[];
}

export const PortfolioWidget = ({ works }: PortfolioWidgetProps) => {
  const { t } = useTranslation();
  const [planFact, setPlanFact] = useState<PlanFact>('plan');
  const [sumCount, setSumCount] = useState<SumCount>('sum');

  const valueOf = (slice: ProjectsSlice): number => {
    if (sumCount === 'sum') {
      return (planFact === 'plan' ? slice.planSumMln : slice.factSumMln) ?? 0;
    }
    return (planFact === 'plan' ? slice.planCount : slice.factCount) ?? 0;
  };

  const unitLabel = (vMln: number): string => {
    if (sumCount === 'count') {
      return `${fmt(vMln)} ${t('projects.units.contractsShort')}`;
    }
    const money = moneyMln(vMln);
    return `${money.value} ${t(money.mlrd ? 'projects.units.mlrdTg' : 'projects.units.mlnTg')}`;
  };

  const items = works.map((slice, index) => ({
    key: slice.key,
    label: t(`projects.infrastructure.works.${slice.key}`, {
      defaultValue: slice.key,
    }),
    value: valueOf(slice),
    color: CHART_COLORS[index % CHART_COLORS.length],
    valueLabel: unitLabel(valueOf(slice)),
  }));

  const total = items.reduce((acc, item) => acc + item.value, 0);
  const centerMoney = moneyMln(total);

  return (
    <Card className="shadow-card" data-testid="projects-portfolio">
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle>{t('projects.infrastructure.portfolio.title')}</CardTitle>
        <div className="flex gap-2">
          <SegToggle<PlanFact>
            ariaLabel={t('projects.infrastructure.seg.planFact')}
            options={[
              { value: 'plan', label: t('projects.infrastructure.seg.plan') },
              { value: 'fact', label: t('projects.infrastructure.seg.fact') },
            ]}
            value={planFact}
            onChange={setPlanFact}
            testId="projects-portfolio-planfact"
          />
          <SegToggle<SumCount>
            ariaLabel={t('projects.infrastructure.seg.sumCount')}
            options={[
              { value: 'sum', label: t('projects.infrastructure.seg.sum') },
              { value: 'count', label: t('projects.infrastructure.seg.count') },
            ]}
            value={sumCount}
            onChange={setSumCount}
            testId="projects-portfolio-sumcount"
          />
        </div>
      </CardHeader>
      <CardContent>
        <DonutChart
          items={items}
          centerValue={
            sumCount === 'sum' ? centerMoney.value : fmt1(total)
          }
          centerLabel={`${t(
            sumCount === 'sum'
              ? centerMoney.mlrd
                ? 'projects.units.mlrdTg'
                : 'projects.units.mlnTg'
              : 'projects.infrastructure.portfolio.contracts',
          )} · ${t(`projects.infrastructure.seg.${planFact}`).toLowerCase()}`}
          testId="projects-portfolio-donut"
        />
      </CardContent>
    </Card>
  );
};
